import axios, { AxiosResponse } from 'axios';
import { FCAExistenceRequest, FCAExistenceResponse } from '../types';
import { ENDPOINTS } from '../config/endpoints';

// Azure Function endpoint for checking FCA existence
const FCA_EXISTENCE_ENDPOINT = process.env.REACT_APP_FCA_EXISTENCE_ENDPOINT ||
  ENDPOINTS.CHECK_FCA_EXISTENCE;

// Timeout for API calls
const API_TIMEOUT = 10000;

// Create axios instance with default config
const apiClient = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Checks if an FCA number already exists in Smartr365 system
 * @param fcaNumber - The FCA number to check
 * @returns Promise with existence check response
 */
export const checkFCAExistence = async (fcaNumber: string): Promise<FCAExistenceResponse> => {
  try {
    // Clean and validate input
    const cleanFcaNumber = fcaNumber.trim().replace(/\s+/g, '');
    
    if (!cleanFcaNumber) {
      throw new Error('FCA number is required');
    }

    const requestData: FCAExistenceRequest = {
      fca_number: cleanFcaNumber
    };

    console.log('Checking FCA existence for:', cleanFcaNumber);

    const response: AxiosResponse<FCAExistenceResponse> = await apiClient.post(
      FCA_EXISTENCE_ENDPOINT,
      requestData
    );

    console.log('FCA existence check response:', response.data);

    return response.data;

  } catch (error) {
    console.error('Error checking FCA existence:', error);
    
    // Handle different error types
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data as FCAExistenceResponse;
        return {
          fca_number: fcaNumber,
          exists: false,
          service_available: false,
          message: errorData.message || `Server error: ${error.response.status}`,
          existing_firms: [],
          error: errorData.error || error.message
        };
      } else if (error.request) {
        // Request was made but no response received
        return {
          fca_number: fcaNumber,
          exists: false,
          service_available: false,
          message: 'Unable to connect to FCA existence check service',
          existing_firms: [],
          error: 'Network error'
        };
      }
    }

    // Generic error
    return {
      fca_number: fcaNumber,
      exists: false,
      service_available: false,
      message: 'Unexpected error checking FCA existence',
      existing_firms: [],
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Formats existing firms information for display
 * @param existingFirms - Array of existing firms
 * @returns Formatted string for display
 */
export const formatExistingFirms = (existingFirms: FCAExistenceResponse['existing_firms']): string => {
  if (!existingFirms || existingFirms.length === 0) {
    return '';
  }

  if (existingFirms.length === 1) {
    const firm = existingFirms[0];
    return `"${firm.company_name}" (${firm.subdomain ? firm.subdomain + '.app.smartr365.com' : 'No subdomain'})`;
  }

  return existingFirms.map((firm, index) => 
    `${index + 1}. "${firm.company_name}" (${firm.subdomain ? firm.subdomain + '.app.smartr365.com' : 'No subdomain'})`
  ).join('\n');
};

/**
 * Checks if FCA existence check is enabled
 * @returns boolean indicating if the service is enabled
 */
export const isFCAExistenceCheckEnabled = (): boolean => {
  return !!FCA_EXISTENCE_ENDPOINT;
};
